import type { JSX } from 'react';
import { Badge, Box, Text } from '@ksyos/design-system';
import { useTheme } from 'styled-components';
import type { IStep, IStepCount } from '@common/interfaces/api/outclinicDashboardApi';

type outClinicDashboardStepFilterItemProps = {
    activeDashboardStepIdentifier: string | null;
    className?: string;
    counts: IStepCount[];
    onChange: (dashboardStepIdentifier: string) => void;
    steps: IStep[];
    title: string;
};

export function OutclinicDashboardStepCardFilterItem(
    props: Readonly<outClinicDashboardStepFilterItemProps>
): JSX.Element {
    const theme = useTheme();

    const baseStyles = {
        '&:focus': {
            outline: 0,
        },
        '&:hover': {
            borderColor: theme.colors.neutral300,
        },
        borderColor: theme.colors.transparent,
        boxShadow: `inset 0 0 0 ${theme.colors.blue700}, ${theme.shadows.base}`,
        outline: 0,
        transition: 'all 150ms ease',
    };

    const styles = {
        ...baseStyles,
        '&:hover': {
            borderColor: theme.colors.blue700,
        },
        borderColor: theme.colors.blue700,
        boxShadow: `inset 3px 0 0 ${theme.colors.blue700}, ${theme.shadows.base}`,
    };

    return (
        <Box
            alignItems="center"
            className={props.className}
            display="flex"
            flexDirection="column"
            position="relative"
            zIndex={0}
        >
            <Text
                mb={5}
                size="xl"
                sx={{ minHeight: '28px' }}
                textAlign="center"
            >
                {props.title}
            </Text>
            <Box
                className="outclinic-dashboard-step-card-filter-item-steps"
                display="flex"
                flexDirection="row"
                gap={2}
                position="relative"
            >
                {props.steps.map((step) => {
                    const { overdue, total } = props.counts.find(
                        (count) => count.dashboardStepIdentifier === step.dashboardStepIdentifier
                    ) || {
                        overdue: 0,
                        total: 0,
                    };

                    return (
                        <Box
                            as="button"
                            backgroundColor="white"
                            border="1px solid transparent"
                            borderRadius="base"
                            display="flex"
                            flexDirection="column"
                            height="185px"
                            key={step.id}
                            onClick={() => props.onChange(step.dashboardStepIdentifier)}
                            paddingX={3}
                            paddingY={3}
                            sx={
                                step.dashboardStepIdentifier === props.activeDashboardStepIdentifier
                                    ? styles
                                    : baseStyles
                            }
                            textAlign={'center'}
                            width="152px"
                        >
                            <Text
                                fontSize="xl"
                                fontWeight="medium"
                                sx={{ alignSelf: 'center' }}
                            >
                                {total}
                            </Text>
                            <Text
                                color="subtle"
                                size="sm"
                                sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {step.dashboardName}
                            </Text>
                            {overdue > 0 && (
                                <Badge
                                    color="orange"
                                    size="small"
                                    sx={{
                                        mt: 2,
                                    }}
                                    text={`${overdue} ${step.dueDescription ? step.dueDescription : 'te laat'}`}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
